/* dashboard-rediseno (15-oct-2026): EL MENU DE TRES PUNTOS DE LA TARJETA.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Seccion "Menu (Rename y Delete)" del documento: un boton de 40 x 40 (44 de toque en el
   telefono) arriba a la derecha de la tarjeta; abre debajo, alineado a la derecha, una caja de
   184 px (fondo #1B1B1B, borde #2C2C2C, radio 12) con opciones de 44 px con su icono; al pasar
   el raton, fondo #262626. Lo que trae cada tarjeta lo decide el Panel (la tabla "Que trae el
   ..."): esta pieza solo lo pinta.

   - `apagado`: el arranque de la app (A18): el boton no abre hasta saber el estado.
   - `sinRed`: la app sin conexion (A17): las opciones se ven al 38 % y al tocarlas no hacen lo
     suyo: avisan "You need a connection" (`alSinRed`, el aviso de hoy).
   - Se cierra al tocar fuera, al tocar otra vez el boton, con Esc y al elegir una opcion; hay
     uno abierto a la vez (`unoALaVez.js`); las flechas recorren las opciones, Tab sigue el
     orden normal y al cerrar con Esc el foco vuelve al boton.

   Las opciones viven SIEMPRE en el arbol, y cerrado su caja lleva `hidden` (no se ve, no se
   enfoca y el lector de pantalla no la lee): asi la tarjeta no cambia de arbol al abrir y
   cerrar, y cada opcion conserva su marca de siempre (`delete-<id>`). */
import React, { useCallback, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useCierreDeFlotante } from './unoALaVez';
import { AreaDe44 } from './anatomia';

export const OPACIDAD_SIN_RED = 0.38;
export const CAJA_DEL_MENU = 'absolute right-0 top-full mt-1 z-40 w-[184px] rounded-xl border border-[#2C2C2C] bg-[#1B1B1B] py-1 shadow-xl';
// El fondo #262626 al pasar el raton (gateado: `hoverable`, R-3) y el MISMO al apretar con el dedo, sin
// esperar la transicion (R-5).
export const OPCION_DEL_MENU = 'w-full h-11 px-3 flex items-center gap-2.5 text-left text-[14px] hoverable:bg-[#262626] active:bg-[#262626] active:duration-0 focus-visible:bg-[#262626] focus:outline-none';

export default function MenuDeTresPuntos({
  testid, etiqueta, opciones = [], apagado = false, sinRed = false, alSinRed, tactil = false, botonRef: botonExterno,
}) {
  const [abierto, setAbierto] = useState(false);
  const cajaRef = useRef(null);
  const botonPropio = useRef(null);
  const botonRef = botonExterno || botonPropio;
  const cerrar = useCallback(() => setAbierto(false), []);
  useCierreDeFlotante(abierto, cerrar, cajaRef, botonRef);

  const enfocarOpcion = (paso) => {
    const caja = cajaRef.current;
    if (!caja) return;
    const items = [...caja.querySelectorAll('[role="menuitem"]')];
    if (items.length === 0) return;
    const actual = items.indexOf(document.activeElement);
    const siguiente = actual < 0 ? 0 : (actual + paso + items.length) % items.length;
    items[siguiente].focus();
  };

  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        ref={botonRef}
        type="button"
        disabled={apagado}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
        onClick={() => setAbierto((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && abierto) { e.preventDefault(); enfocarOpcion(1); }
        }}
        className="relative w-10 h-10 -mr-2 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:bg-[#1B1B1B] hoverable:text-[#F4F4F3] active:bg-[#1B1B1B] disabled:opacity-40"
        data-testid={testid}
      >
        {tactil ? <AreaDe44 /> : null}
        <MoreHorizontal className="w-5 h-5" aria-hidden="true" />
      </button>
      <div
        ref={cajaRef}
        role="menu"
        hidden={!abierto}
        className={CAJA_DEL_MENU}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); enfocarOpcion(1); }
          if (e.key === 'ArrowUp') { e.preventDefault(); enfocarOpcion(-1); }
        }}
        data-testid={`${testid}-opciones`}
      >
        {opciones.map((op) => {
          const Icono = op.icono;
          return (
            <React.Fragment key={op.testid}>
              {op.separadorAntes ? <div className="my-1 h-px bg-[#2C2C2C]" aria-hidden="true" /> : null}
              <button
                type="button"
                role="menuitem"
                disabled={!!op.apagado}
                aria-disabled={sinRed || undefined}
                style={sinRed ? { opacity: OPACIDAD_SIN_RED } : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  setAbierto(false);
                  if (sinRed) { if (alSinRed) alSinRed(); return; }
                  if (op.alTocar) op.alTocar(botonRef);
                }}
                className={`${OPCION_DEL_MENU} ${op.peligro ? 'text-[#F87171]' : 'text-[#F4F4F3]'} disabled:opacity-40`}
                data-testid={op.testid}
              >
                {Icono ? <Icono className="w-4 h-4 shrink-0" aria-hidden="true" /> : null}
                <span className="truncate">{op.texto}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
